import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { ExternalLink, Building2, ArrowRight, Loader2 } from 'lucide-react';
import api from '../components/api';
import Layout from '../components/Layout';
import PageBanner from '../components/Banner';

const CARD_COLORS = [
  { accent: '#E8611A', light: 'rgba(232,97,26,0.08)', border: 'rgba(232,97,26,0.20)' },
  { accent: '#3b82f6', light: 'rgba(59,130,246,0.08)', border: 'rgba(59,130,246,0.20)' },
  { accent: '#16a34a', light: 'rgba(22,163,74,0.08)',  border: 'rgba(22,163,74,0.20)'  },
];

const ReferenceCard = ({ reference, index }) => {
  const [hovered, setHovered] = useState(false);
  const color = CARD_COLORS[index % CARD_COLORS.length];

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: hovered ? 'rgba(255,255,255,0.98)' : 'rgba(255,255,255,0.82)',
        backdropFilter: 'blur(14px)',
        border: hovered ? `1px solid ${color.border}` : '1px solid rgba(0,0,0,0.06)',
        borderRadius: 20,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        transition: 'transform .25s, box-shadow .25s, border-color .25s, background .25s',
        transform: hovered ? 'translateY(-6px)' : 'none',
        boxShadow: hovered
          ? `0 20px 48px rgba(0,0,0,0.10), 0 0 0 1px ${color.border}`
          : '0 4px 16px rgba(0,0,0,0.06)',
      }}
    >
      {/* Image / placeholder */}
      <div style={{
        height: 180, flexShrink: 0,
        background: reference.image
          ? `url(${reference.image}) center/cover no-repeat`
          : `linear-gradient(135deg,${color.light.replace('0.08','0.18')} 0%,rgba(248,250,252,1) 100%)`,
        position: 'relative',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {!reference.image && (
          <div style={{ width:64, height:64, borderRadius:16, background:color.light, border:`1.5px solid ${color.border}`, display:'flex', alignItems:'center', justifyContent:'center' }}>
            <Building2 size={28} color={color.accent} />
          </div>
        )}
        <div style={{ position:'absolute', top:14, left:14, width:32, height:32, borderRadius:'50%', background:color.accent, color:'#fff', fontSize:'0.72rem', fontWeight:800, display:'flex', alignItems:'center', justifyContent:'center', boxShadow:`0 2px 8px ${color.accent}40` }}>
          {String(index + 1).padStart(2,'0')}
        </div>
        {reference.lien && (
          <a href={reference.lien} target="_blank" rel="noopener noreferrer"
            onClick={e => e.stopPropagation()}
            style={{ position:'absolute', top:14, right:14, width:32, height:32, borderRadius:'50%', background:'rgba(255,255,255,0.9)', display:'flex', alignItems:'center', justifyContent:'center', boxShadow:'0 2px 8px rgba(0,0,0,0.10)', textDecoration:'none', transition:'background .2s' }}
            onMouseEnter={e => { e.currentTarget.style.background=color.accent; }}
            onMouseLeave={e => { e.currentTarget.style.background='rgba(255,255,255,0.9)'; }}
          >
            <ExternalLink size={14} color={color.accent} />
          </a>
        )}
      </div>

      {/* Content */}
      <div style={{ padding:'22px 24px 24px', display:'flex', flexDirection:'column', gap:12, flex:1 }}>
        <div style={{ display:'inline-flex', alignItems:'center', gap:7, padding:'5px 12px', borderRadius:999, width:'fit-content', background:color.light, border:`1px solid ${color.border}` }}>
          <Building2 size={11} color={color.accent} />
          <span style={{ fontSize:'0.7rem', fontWeight:700, color:color.accent, letterSpacing:'0.05em', textTransform:'uppercase' }}>
            {reference.client}
          </span>
        </div>
        <p style={{ fontSize:'0.875rem', color:'#475569', lineHeight:1.7, margin:0, flex:1, display:'-webkit-box', WebkitLineClamp:4, WebkitBoxOrient:'vertical', overflow:'hidden' }}>
          {reference.mission}
        </p>
        {reference.lien && (
          <a href={reference.lien} target="_blank" rel="noopener noreferrer"
            style={{ display:'inline-flex', alignItems:'center', gap:6, fontSize:'0.8rem', fontWeight:600, color:color.accent, textDecoration:'none', marginTop:4, transition:'gap .2s' }}
            onMouseEnter={e => { e.currentTarget.style.gap='10px'; }}
            onMouseLeave={e => { e.currentTarget.style.gap='6px'; }}
          >
            Voir le projet <ArrowRight size={14} />
          </a>
        )}
      </div>
    </div>
  );
};

const SkeletonCard = () => (
  <div style={{ borderRadius:20, overflow:'hidden', background:'rgba(255,255,255,0.7)', border:'1px solid rgba(0,0,0,0.06)', boxShadow:'0 4px 16px rgba(0,0,0,0.05)' }}>
    <div style={{ height:180, background:'linear-gradient(90deg,#f1f5f9 25%,#e2e8f0 50%,#f1f5f9 75%)', backgroundSize:'200% 100%', animation:'shimmer 1.4s infinite' }} />
    <div style={{ padding:'22px 24px 24px', display:'flex', flexDirection:'column', gap:12 }}>
      <div style={{ height:24, width:'40%', borderRadius:999, background:'#f1f5f9' }} />
      <div style={{ height:14, width:'100%', borderRadius:6, background:'#f1f5f9' }} />
      <div style={{ height:14, width:'85%', borderRadius:6, background:'#f1f5f9' }} />
      <div style={{ height:14, width:'70%', borderRadius:6, background:'#f1f5f9' }} />
    </div>
  </div>
);

export default function ReferencesPage() {
  const [references, setReferences] = useState([]);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [total, setTotal] = useState(0);
  const sentinelRef = useRef(null);

  const fetchPage = useCallback((pageNum, append = false) => {
    if (append) setLoadingMore(true);
    else setLoading(true);

    api.get('/aydees/liste-reference/', { params: { page: pageNum, page_size: 15 } })
      .then(res => {
        const data = res.data;
        const results = data.results || data || [];
        setTotal(data.count || results.length);
        setHasNext(!!data.next);
        setReferences(prev => append ? [...prev, ...results] : results);
        setPage(pageNum);
      })
      .catch(() => {})
      .finally(() => { setLoading(false); setLoadingMore(false); });
  }, []);

  useEffect(() => {
    fetchPage(1, false);
  }, [fetchPage]);

  // Infinite scroll sentinel
  useEffect(() => {
    if (!sentinelRef.current) return;
    const observer = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasNext && !loadingMore) {
        fetchPage(page + 1, true);
      }
    }, { threshold: 0.1 });
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasNext, loadingMore, page, fetchPage]);

  return (
    <Layout>
      <Helmet>
        <title>Nos Références | Aydees</title>
        <meta name="description" content="Découvrez les projets et réalisations d'Aydees — solutions IT & conseils au Niger." />
      </Helmet>

      <style>{`
        @keyframes shimmer {
          0%   { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>

      <PageBanner
        title="Nos Références"
        subtitle="Découvrez nos projets et partenariats qui témoignent de notre expertise en solutions IT au Niger."
      />

      {/* Grid */}
      <section style={{ background:'linear-gradient(180deg,#f8fafc 0%,#ffffff 100%)', padding:'48px 24px 64px', fontFamily:"'DM Sans',sans-serif", minHeight:400 }}>
        <div style={{ maxWidth:1100, margin:'0 auto' }}>

          {loading ? (
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))', gap:24 }}>
              {Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)}
            </div>
          ) : references.length === 0 ? (
            <div style={{ textAlign:'center', padding:'80px 0', display:'flex', flexDirection:'column', alignItems:'center', gap:16 }}>
              <div style={{ width:72, height:72, borderRadius:'50%', background:'rgba(232,97,26,0.08)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                <Building2 size={32} color="#E8611A" />
              </div>
              <p style={{ fontSize:'1rem', color:'#64748b', margin:0 }}>Aucune référence disponible pour le moment.</p>
            </div>
          ) : (
            <>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))', gap:24 }}>
                {references.map((ref, i) => (
                  <ReferenceCard key={ref.id} reference={ref} index={i} />
                ))}
              </div>

              {/* Sentinel pour infinite scroll */}
              <div ref={sentinelRef} style={{ height:1 }} />

              {/* Loader "charger plus" */}
              {loadingMore && (
                <div style={{ display:'flex', justifyContent:'center', alignItems:'center', gap:10, padding:'32px 0', color:'#64748b', fontSize:'0.9rem' }}>
                  <Loader2 size={20} color="#E8611A" style={{ animation:'spin 1s linear infinite' }} />
                  Chargement…
                </div>
              )}

              {/* Message fin de liste */}
              {!hasNext && references.length > 0 && (
                <div style={{ textAlign:'center', padding:'36px 0 0', color:'#94a3b8', fontSize:'0.82rem' }}>
                  — {references.length} référence{references.length > 1 ? 's' : ''} au total —
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </Layout>
  );
}
